// File: src/blueprint/components/content/question/QuestionContent.jsx

/**
 * GOAL:
 * - Render question steps (single/multi/text/rank).
 * - IMPORTANT UX:
 *   - text/textarea answers are only committed to global state on blur (focus loss),
 *     so progressive insights won't fire on every keystroke.
 *
 * @author GoViral Web
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { ACTIONS } from '../../state/blueprintReducer.js';
import { selectActiveModule, selectActiveTrackId } from '../../state/selectors.js';
import { useBlueprintState } from '../../state/useBlueprintState.js';

function asArray(v) {
  return Array.isArray(v) ? v : [];
}

function getByPath(obj, path) {
  if (!obj || typeof obj !== 'object') return undefined;
  if (!path || typeof path !== 'string') return undefined;

  return path
    .split('.')
    .map((part) => part.trim())
    .filter(Boolean)
    .reduce((current, part) => {
      if (!current || typeof current !== 'object') return undefined;
      return current[part];
    }, obj);
}

function normalizeChoices(item) {
  const raw = asArray(item?.choices);

  return raw
    .map((c, idx) => {
      if (!c || typeof c !== 'object') return null;

      const id = c?.id != null ? String(c.id).trim() : '';
      const label = c?.label != null ? String(c.label) : '';
      const explanation = c?.explanation != null ? String(c.explanation) : '';
      const safeLabel = label || id || `Option ${idx + 1}`;
      const key = id || safeLabel;

      return {
        id: key,
        label: safeLabel,
        explanation,
        category: c?.category != null ? String(c.category) : '',
        imageKey: c?.imageKey != null ? String(c.imageKey) : '',
        imageUrl: c?.imageUrl != null ? String(c.imageUrl) : '',
        examples: c?.examples != null ? String(c.examples) : '',
      };
    })
    .filter(Boolean);
}

function normalizeMatrixRows(item) {
  const raw = asArray(item?.rows);

  return raw
    .map((c, idx) => {
      if (!c || typeof c !== 'object') return null;

      const id = c?.id != null ? String(c.id).trim() : '';
      const label = c?.label != null ? String(c.label) : '';
      const explanation = c?.explanation != null ? String(c.explanation) : '';

      const safeLabel = label || id || `Row ${idx + 1}`;
      const key = id || safeLabel;

      return {
        id: key,
        label: safeLabel,
        explanation,
      };
    })
    .filter(Boolean);
}

function normalizeMatrixColumns(item) {
  const raw = asArray(item?.columns);

  return raw
    .map((c, idx) => {
      if (!c || typeof c !== 'object') return null;

      const id = c?.id != null ? String(c.id).trim() : '';
      const label = c?.label != null ? String(c.label) : '';
      const explanation = c?.explanation != null ? String(c.explanation) : '';

      const safeLabel = label || id || `Column ${idx + 1}`;
      const key = id || safeLabel;

      return {
        id: key,
        label: safeLabel,
        explanation,
      };
    })
    .filter(Boolean);
}

function normalizeActions(item) {
  const raw = asArray(item?.actions);

  const fallback = [
    { id: 'yes', label: 'Yes', value: 2 },
    { id: 'maybe', label: 'Maybe', value: 1 },
    { id: 'no', label: 'No', value: 0 },
  ];

  const source = raw.length > 0 ? raw : fallback;

  return source
    .map((action) => {
      if (!action || typeof action !== 'object') return null;

      const id = action?.id != null ? String(action.id).trim() : '';
      const label = action?.label != null ? String(action.label) : id;
      const value = Number(action?.value ?? 0);

      if (!id) return null;

      return { id, label, value };
    })
    .filter(Boolean);
}

function shouldUseTextarea(item) {
  const ui = item?.ui && typeof item.ui === 'object' ? item.ui : null;

  if (ui?.input === 'textarea') return true;
  if (ui?.type === 'textarea') return true;
  if (ui?.multiline === true) return true;
  if (item?.multiline === true) return true;
  if (item?.textarea === true) return true;

  const rows = Number(ui?.rows ?? item?.rows ?? 0);
  return rows >= 2;
}

function makeAnswerKey(packId, moduleId, trackId, stepId) {
  const p = String(packId || '').trim();
  const m = String(moduleId || '').trim();
  const t = String(trackId || '').trim();
  const s = String(stepId || '').trim();

  if (!p || !m || !t || !s) return s;

  return `${p}::${m}::${t}::${s}`;
}

export default function QuestionContent({ item }) {
  const { state, dispatch } = useBlueprintState();

  const packId = String(state?.packId || 'none').trim();
  const module = selectActiveModule(state);
  const activeTrackId = selectActiveTrackId(state);
  const trackId = activeTrackId || module?.default_track_id || 'quick';

  const stepId = String(item?.id ?? '').trim();
  const answerKey = makeAnswerKey(packId, module?.id, trackId, stepId);

  const type = item?.inputType ? String(item.inputType) : item?.type ? String(item.type) : '';

  const title = item?.title ? String(item.title) : 'Question';
  const explainQuestion = item?.explanation ? String(item.explanation) : '';
  const instruction = item?.instruction ? String(item.instruction) : '';

  const isText = type === 'text';
  const useTextarea = isText && shouldUseTextarea(item);
  const isTextareaType = type === 'textarea';
  const isTextLike = isText || isTextareaType;
  const isMatrixSingle = type === 'matrix_single';
  const isSelectCards = type === 'select_cards';

  const allAnswers = state?.answers && typeof state.answers === 'object' ? state.answers : {};
  const committedValueRaw = allAnswers[answerKey];

  const committedTextValue = isTextLike
    ? typeof committedValueRaw === 'string'
      ? committedValueRaw
      : committedValueRaw != null
        ? String(committedValueRaw)
        : ''
    : '';

  const [draft, setDraft] = useState(isTextLike ? committedTextValue : '');
  const [activeExplanationId, setActiveExplanationId] = useState('');
  const [activeMatrixRowId, setActiveMatrixRowId] = useState('');
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isTextLike) return;
    setDraft(committedTextValue);
    isFocusedRef.current = false;
  }, [isTextLike, committedTextValue, stepId, answerKey]);

  useEffect(() => {
    if (!isTextLike) return;
    if (isFocusedRef.current) return;
    setDraft(committedTextValue);
  }, [isTextLike, committedTextValue]);

  function dispatchAnswer(value) {
    if (!stepId) return;

    dispatch({
      type: ACTIONS.SET_ANSWER,
      payload: {
        stepId,
        answerKey,
        packId,
        moduleId: module?.id || '',
        trackId,
        value,
      },
    });
  }

  function commitDraft(value) {
    const v = typeof value === 'string' ? value : String(value ?? '');
    dispatchAnswer(v.trim());
  }

  const staticChoices = useMemo(() => normalizeChoices(item), [item]);

  const sourcedChoices = useMemo(() => {
    const optionsSource = typeof item?.optionsSource === 'string' ? item.optionsSource.trim() : '';

    if (!optionsSource) {
      return [];
    }

    const resolvedOptions = getByPath(state, optionsSource);

    return normalizeChoices({
      choices: Array.isArray(resolvedOptions) ? resolvedOptions : [],
    });
  }, [item, state]);

  console.log('[QuestionContent] optionsSource', item?.optionsSource);
  console.log('[QuestionContent] state.resolved', state?.resolved);
  console.log('[QuestionContent] state.progressive', state?.progressive);
  console.log('[QuestionContent] direct resolvedOptions', getByPath(state, item?.optionsSource));

  const choices = staticChoices.length > 0 ? staticChoices : sourcedChoices;
  const actions = useMemo(() => normalizeActions(item), [item]);

  const isWaitingForDynamicOptions =
    Boolean(item?.optionsSource) && staticChoices.length === 0 && sourcedChoices.length === 0;
  const matrixRows = useMemo(() => normalizeMatrixRows(item), [item]);
  const matrixColumns = useMemo(() => normalizeMatrixColumns(item), [item]);

  const multiple = type === 'multi';
  const min = Number(item?.min ?? (multiple ? 1 : 1)) || (multiple ? 1 : 1);
  const max =
    Number(item?.max ?? (multiple ? choices.length : 1)) || (multiple ? choices.length : 1);

  const selectedKeys = useMemo(() => {
    if (!committedValueRaw) return [];
    if (Array.isArray(committedValueRaw)) return committedValueRaw.map(String);
    return [String(committedValueRaw)];
  }, [committedValueRaw]);

  const matrixValue =
    isMatrixSingle &&
    committedValueRaw &&
    typeof committedValueRaw === 'object' &&
    !Array.isArray(committedValueRaw)
      ? committedValueRaw
      : {};

  function isSelected(key) {
    return selectedKeys.includes(String(key));
  }

  function toggleChoice(key) {
    const k = String(key);
    if (!stepId) return;

    if (!multiple) {
      dispatchAnswer(k);
      return;
    }

    const next = selectedKeys.slice();

    if (next.includes(k)) {
      dispatchAnswer(next.filter((x) => x !== k));
      return;
    }

    if (next.length >= max) return;

    next.push(k);
    dispatchAnswer(next);
  }

  function toggleMatrixValue(rowId, columnId) {
    const rowKey = String(rowId);
    const columnKey = String(columnId);

    const next = {
      ...matrixValue,
    };

    if (next[rowKey] === columnKey) {
      delete next[rowKey];
    } else {
      next[rowKey] = columnKey;
    }

    const selectedCount = Object.keys(next).length;

    if (selectedCount > max) {
      return;
    }

    dispatchAnswer(next);
  }

  const selectCardsValue =
    isSelectCards &&
    committedValueRaw &&
    typeof committedValueRaw === 'object' &&
    !Array.isArray(committedValueRaw)
      ? committedValueRaw
      : {};

  function getSelectCardsSelectedCount(nextValue = selectCardsValue) {
    return Object.values(nextValue).filter((actionId) => actionId === 'yes' || actionId === 'maybe')
      .length;
  }

  function selectCardAction(choiceId, actionId) {
    const choiceKey = String(choiceId);
    const actionKey = String(actionId);

    const next = {
      ...selectCardsValue,
      [choiceKey]: actionKey,
    };

    if (getSelectCardsSelectedCount(next) > max) {
      return;
    }

    dispatchAnswer(next);
  }

  if (isText || isTextareaType) {
    const ui = item?.ui && typeof item.ui === 'object' ? item.ui : {};
    const placeholder =
      typeof ui?.placeholder === 'string'
        ? ui.placeholder
        : typeof item?.placeholder === 'string'
          ? item.placeholder
          : '';

    const renderTextarea = isTextareaType || useTextarea;
    const rows = Math.max(2, Number(ui?.rows ?? item?.rows ?? (renderTextarea ? 4 : 0)) || 0);

    return (
      <div>
        <p className="dv-br-q__question">{title}</p>
        {instruction ? <p className="dv-br-card__text dv-br-card__muted">{instruction}</p> : null}

        <div style={{ marginTop: 16 }}>
          {renderTextarea ? (
            <textarea
              className="dv-br-q__textInput"
              placeholder={placeholder}
              rows={rows}
              value={draft}
              onChange={(e) => setDraft(String(e?.target?.value ?? ''))}
              onFocus={() => {
                isFocusedRef.current = true;
              }}
              onBlur={(e) => {
                isFocusedRef.current = false;
                commitDraft(String(e?.target?.value ?? ''));
              }}
              style={{
                width: '100%',
                borderRadius: 14,
                padding: 12,
                border: '1px solid rgba(0,0,0,0.18)',
                background: 'rgba(0,0,0,0.03)',
                resize: 'vertical',
              }}
            />
          ) : (
            <input
              className="dv-br-q__textInput"
              type="text"
              placeholder={placeholder}
              value={draft}
              onChange={(e) => setDraft(String(e?.target?.value ?? ''))}
              onFocus={() => {
                isFocusedRef.current = true;
              }}
              onBlur={(e) => {
                isFocusedRef.current = false;
                commitDraft(String(e?.target?.value ?? ''));
              }}
              style={{
                width: '100%',
                borderRadius: 14,
                padding: 12,
                border: '1px solid rgba(0,0,0,0.18)',
                background: 'rgba(0,0,0,0.03)',
              }}
            />
          )}
        </div>

        <p className="dv-br-card__text dv-br-card__muted" style={{ marginTop: 12 }}>
          Tip: your answer is saved when you leave the field.
        </p>
      </div>
    );
  }
  const [selectCardIndex, setSelectCardIndex] = useState(0);
  const [selectCardAnimation, setSelectCardAnimation] = useState('');
  if (isSelectCards) {
    const currentChoice = choices[selectCardIndex] || choices[0];

    const cardStats = choices.reduce(
      (stats, choice) => {
        const action = selectCardsValue[choice.id] || '';

        if (action === 'yes') {
          stats.yes += 1;
        } else if (action === 'maybe') {
          stats.maybe += 1;
        } else if (action === 'no') {
          stats.no += 1;
        } else {
          stats.unanswered += 1;
        }

        return stats;
      },
      {
        yes: 0,
        maybe: 0,
        no: 0,
        unanswered: 0,
      }
    );

    const answeredCount = cardStats.yes + cardStats.maybe + cardStats.no;

    const goPrevCard = () => {
      setSelectCardIndex((current) => (current <= 0 ? choices.length - 1 : current - 1));
    };

    const goNextCard = () => {
      setSelectCardIndex((current) => (current >= choices.length - 1 ? 0 : current + 1));
    };

    const findNextUnansweredCardIndex = (nextValue = selectCardsValue) => {
      const nextIndex = choices.findIndex((choice, index) => {
        return index > selectCardIndex && !nextValue[choice.id];
      });

      if (nextIndex >= 0) {
        return nextIndex;
      }

      return choices.findIndex((choice) => !nextValue[choice.id]);
    };

    const slideToCard = (nextIndex) => {
      if (nextIndex < 0 || nextIndex === selectCardIndex) {
        return;
      }

      setSelectCardAnimation('is-answer-saved');

      window.setTimeout(() => {
        setSelectCardAnimation('is-slide-out-left');

        window.setTimeout(() => {
          setSelectCardIndex(nextIndex);
          setSelectCardAnimation('is-slide-in-right');

          window.setTimeout(() => {
            setSelectCardAnimation('');
          }, 260);
        }, 260);
      }, 180);
    };

    const goNextUnansweredCard = (nextValue = selectCardsValue) => {
      slideToCard(findNextUnansweredCardIndex(nextValue));
    };

    const handleSelectCardAction = (choiceId, actionId) => {
      const choiceKey = String(choiceId);
      const actionKey = String(actionId);

      const nextValue = {
        ...selectCardsValue,
        [choiceKey]: actionKey,
      };

      dispatchAnswer(nextValue);
      goNextUnansweredCard(nextValue);
    };

    if (!currentChoice) {
      return null;
    }

    const activeAction = selectCardsValue[currentChoice.id] || '';

    return (
      <div>
        <p className="dv-br-q__question">{title}</p>

        {instruction ? <p className="dv-br-card__text">{instruction}</p> : null}

        <div className="dv-br-select-card-carousel">
          <button
            type="button"
            className="dv-br-select-card-carousel__nav"
            onClick={goPrevCard}
            aria-label="Previous card"
          >
            ‹
          </button>

          <article
            className={`dv-br-select-card ${activeAction ? `is-${activeAction}` : ''} ${selectCardAnimation}`}
          >
            <div
              className="dv-br-select-card__body"
              style={
                currentChoice.imageUrl
                  ? { backgroundImage: `url(${currentChoice.imageUrl})` }
                  : undefined
              }
            >
              <div className="dv-br-select-card__overlay">
                <p className="dv-br-select-card__counter">
                  {selectCardIndex + 1} / {choices.length}
                </p>

                <h3>{currentChoice.label}</h3>

                {currentChoice.examples ? (
                  <p className="dv-br-card__text">{currentChoice.examples}</p>
                ) : null}

                <div className="dv-br-select-card__actions">
                  {actions.map((action) => {
                    const active = activeAction === action.id;

                    return (
                      <button
                        key={action.id}
                        type="button"
                        className={`dv-br-q__optionBtn ${active ? 'is-selected' : ''}`}
                        aria-pressed={active}
                        onClick={() => handleSelectCardAction(currentChoice.id, action.id)}
                      >
                        {action.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </article>

          <button
            type="button"
            className="dv-br-select-card-carousel__nav"
            onClick={goNextCard}
            aria-label="Next card"
          >
            ›
          </button>
        </div>

        <div className="dv-br-select-card__stats">
          <p>
            answered: <strong>{answeredCount}</strong> / {choices.length}
          </p>

          <p>
            yes: <strong>{cardStats.yes}</strong> — maybe: <strong>{cardStats.maybe}</strong> — no:{' '}
            <strong>{cardStats.no}</strong> — unanswered:{' '}
            <button type="button" onClick={() => goNextUnansweredCard()}>
              {cardStats.unanswered}
            </button>
          </p>
        </div>
      </div>
    );
  }

  if (isMatrixSingle) {
    const selectedCount = Object.keys(matrixValue).length;

    return (
      <div>
        <p className="dv-br-q__question">
          {title}

          {activeExplanationId === 'question' && explainQuestion ? (
            <span className="question-explanation">{explainQuestion}</span>
          ) : null}
        </p>

        {instruction ? <p className="dv-br-card__text dv-br-card__muted">{instruction}</p> : null}

        <div
          className="dv-br-matrix"
          style={{
            marginTop: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {matrixRows.map((row) => {
            const activeValue = matrixValue[row.id] || '';
            const selectedColumn = matrixColumns.find((column) => column.id === activeValue);
            const isExpanded = activeMatrixRowId === row.id;
            const explanationId = `matrix-row:${row.id}`;

            return (
              <div
                key={row.id}
                className={`dv-br-matrix__row ${activeValue ? 'is-selected' : ''} ${
                  isExpanded ? 'is-expanded' : ''
                }`}
                style={{
                  border: activeValue
                    ? '1px solid rgba(11,58,82,0.35)'
                    : '1px solid rgba(0,0,0,0.08)',
                  borderRadius: 16,
                  padding: 14,
                  background: activeValue ? 'rgba(11,58,82,0.05)' : 'rgba(0,0,0,0.02)',
                }}
              >
                <div
                  className="dv-br-matrix__rowHeader"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => toggleMatrixRow(row.id)}
                    style={{
                      flex: 1,
                      padding: 0,
                      border: 0,
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <strong>{row.label}</strong>

                    {selectedColumn ? (
                      <span
                        className="dv-br-card__text dv-br-card__muted"
                        style={{
                          display: 'block',
                          marginTop: 4,
                        }}
                      >
                        {selectedColumn.label}
                      </span>
                    ) : null}
                  </button>

                  {row.explanation ? (
                    <button
                      type="button"
                      className="dv-toggle-explanation"
                      title="Show explanation"
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleExplanation(explanationId);
                      }}
                      onBlur={closeExplanation}
                    >
                      ?
                    </button>
                  ) : null}
                </div>

                {activeExplanationId === explanationId && row.explanation ? (
                  <p className="dv-br-card__text dv-br-card__muted" style={{ marginTop: 10 }}>
                    {row.explanation}
                  </p>
                ) : null}

                {isExpanded ? (
                  <div
                    className="dv-br-matrix__levels"
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 10,
                      marginTop: 14,
                    }}
                  >
                    {matrixColumns.map((column) => {
                      const active = activeValue === column.id;

                      return (
                        <button
                          key={`${row.id}:${column.id}`}
                          type="button"
                          className="dv-br-q__optionBtn"
                          aria-pressed={active}
                          onClick={() => {
                            toggleMatrixValue(row.id, column.id);
                            setActiveMatrixRowId('');
                          }}
                          style={{
                            borderColor: active ? 'rgba(11,58,82,0.55)' : 'rgba(0,0,0,0.18)',
                            background: active ? 'rgba(11,58,82,0.08)' : 'rgba(0,0,0,0.03)',
                          }}
                        >
                          {column.label}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <p className="dv-br-card__text dv-br-card__muted" style={{ marginTop: 16 }}>
          Selected: <strong>{selectedCount}</strong> — min {min} / max {max}
        </p>
      </div>
    );
  }

  const selectedCount = selectedKeys.length;

  return (
    <div>
      <p className="dv-br-q__question">
        {title}

        {explainQuestion ? (
          <button
            type="button"
            className="dv-toggle-explanation"
            title="Show explanation"
            id="toggle-explanation"
            onClick={() => toggleExplanation('question')}
            onBlur={closeExplanation}
          >
            ?
          </button>
        ) : null}

        {activeExplanationId === 'question' && explainQuestion ? (
          <span className="question-explanation">{explainQuestion}</span>
        ) : null}
      </p>

      {instruction ? <p className="dv-br-card__text dv-br-card__muted">{instruction}</p> : null}

      {isWaitingForDynamicOptions ? (
        <p className="dv-br-card__text dv-br-card__muted" style={{ marginTop: 16 }}>
          Complete the previous steps first. Your options will appear automatically.
        </p>
      ) : null}

      <div className="dv-br-q__options" style={{ marginTop: 16 }}>
        {!isWaitingForDynamicOptions &&
          choices.map((c) => {
            const key = c.id;
            const active = isSelected(key);
            const explanationId = `option:${key}`;

            return (
              <div key={key} className={`dv-br-q__optionWrap ${active ? 'is-selected' : ''}`}>
                <button
                  type="button"
                  className="dv-br-q__optionBtn"
                  aria-pressed={active}
                  onClick={() => toggleChoice(key)}
                >
                  {c.label}
                </button>

                {c.explanation ? (
                  <button
                    type="button"
                    className="dv-toggle-explanation dv-br-q__optionHelp"
                    title="Show explanation"
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleExplanation(explanationId);
                    }}
                    onBlur={closeExplanation}
                  >
                    ?
                  </button>
                ) : null}

                {activeExplanationId === explanationId && c.explanation ? (
                  <p className="option-explanation">{c.explanation}</p>
                ) : null}
              </div>
            );
          })}
      </div>

      {type === 'multi' ? (
        <p className="dv-br-card__text dv-br-card__muted" style={{ marginTop: 16 }}>
          Selected: <strong>{selectedCount}</strong> — min {min} / max {max}
        </p>
      ) : null}
    </div>
  );

  function toggleExplanation(id) {
    setActiveExplanationId((current) => (current === id ? '' : id));
  }

  function closeExplanation() {
    setActiveExplanationId('');
  }

  function toggleMatrixRow(rowId) {
    setActiveMatrixRowId((current) => (current === rowId ? '' : rowId));
  }
}
